import type { ApprovalDTO, ApprovalQuestion, QuestionAnswer } from "./types";

/** The key "Other" answers under. It is implied on every choice question and never one of the agent's own options. */
export const OTHER_KEY = "__other__";

/**
 * The questions of a request. Asks from before the cards (and proposals answered with the agent's own options) have
 * no `questions`, so one is built from the title and the flat options, and they read the same everywhere.
 */
export function questionsOf(a: Pick<ApprovalDTO, "questions" | "options" | "title">): ApprovalQuestion[] {
  if (a.questions?.length) return a.questions;
  return [{ id: "q1", question: a.title, options: a.options?.length ? a.options.map((o) => ({ key: o.key, label: o.label, description: o.description })) : undefined }];
}

/** Every question answered: what moves a request from pending to answered. */
export function allAnswered(questions: ApprovalQuestion[], answers: Record<string, QuestionAnswer> | undefined): boolean {
  return questions.length > 0 && questions.every((q) => isAnswered(answers?.[q.id]));
}

/** One answer given: at least one key picked, or some text typed. */
export function isAnswered(a: QuestionAnswer | undefined): boolean {
  if (!a) return false;
  if (typeof a.text === "string" && a.text.trim()) return true;
  return Boolean(a.keys?.length);
}

/** What the person answered, in the agent's own words where it offered choices. Empty when it is still waiting. */
export function answerText(q: ApprovalQuestion, a: QuestionAnswer | undefined): string {
  if (!a) return "";
  if (typeof a.text === "string" && a.text.trim()) return a.text.trim();
  const labels = (a.keys ?? []).map((k) => (k === OTHER_KEY ? (a.other?.trim() ? `Other: ${a.other.trim()}` : "Other") : (q.options?.find((o) => o.key === k)?.label ?? k)));
  return labels.join(", ");
}

/** Every answer of a request on one line, for the agent's turn and for a list. */
export function answerSummary(a: Pick<ApprovalDTO, "questions" | "options" | "title" | "answers">): string {
  const questions = questionsOf(a);
  const parts = questions.map((q) => {
    const text = answerText(q, a.answers?.[q.id]);
    return text ? (questions.length > 1 ? `${q.question} → ${text}` : text) : "";
  });
  return parts.filter(Boolean).join(" · ");
}
